import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save, Star, Building, Info } from 'lucide-react';

const AboutAhamVisionMissionPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        content_data: {
            vision: {
                title: '',
                content: '',
                icon: 'Star',
                is_active: true
            },
            mission: {
                title: '',
                content: '',
                icon: 'Building',
                is_active: true
            }
        },
        is_active: true
    });

    const iconOptions = [
        { name: 'Star', icon: <Star size={18} /> },
        { name: 'Building', icon: <Building size={18} /> },
        { name: 'Info', icon: <Info size={18} /> }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('/api/admin/about-aham/section/vision_mission');
            if (response.data.success && response.data.data) {
                const data = response.data.data;
                let content_data = typeof data.content_data === 'string'
                    ? JSON.parse(data.content_data)
                    : (data.content_data || { vision: { title: '', content: '', icon: 'Star', is_active: true }, mission: { title: '', content: '', icon: 'Building', is_active: true } });

                // Ensure is_active exists for both
                if (content_data.vision && content_data.vision.is_active === undefined) content_data.vision.is_active = true;
                if (content_data.mission && content_data.mission.is_active === undefined) content_data.mission.is_active = true;

                setFormData({
                    title: data.title || '',
                    description: data.description || '',
                    content_data: content_data,
                    is_active: data.is_active !== false
                });
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching vision & mission data:', error);
            toast.error('Failed to load data');
            setLoading(false);
        }
    };

    const handleToggle = (section) => {
        setFormData(prev => ({
            ...prev,
            content_data: {
                ...prev.content_data,
                [section]: {
                    ...prev.content_data[section],
                    is_active: !prev.content_data[section].is_active
                }
            }
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('vision_') || name.startsWith('mission_')) {
            const [section, field] = name.split('_');
            setFormData(prev => ({
                ...prev,
                content_data: {
                    ...prev.content_data,
                    [section]: {
                        ...prev.content_data[section],
                        [field]: value
                    }
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const response = await axios.put('/api/admin/about-aham/section/vision_mission', {
                title: formData.title,
                description: formData.description,
                content_data: formData.content_data,
                is_active: formData.is_active
            });

            if (response.data.success) {
                toast.success('Vision & Mission updated successfully');
                fetchData();
            }
        } catch (error) {
            console.error('Error updating vision & mission:', error);
            toast.error('Failed to update vision & mission');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-6">Loading...</div>;

    const StatusToggle = ({ active, onToggle }) => (
        <button
            type="button"
            onClick={onToggle}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${active ? 'bg-primary-600' : 'bg-slate-200'
                }`}
        >
            <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${active ? 'translate-x-5' : 'translate-x-0'
                    }`}
            />
        </button>
    );

    return (
        <div className="max-w-4xl mx-auto space-y-6 pb-12">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">About Aham - Vision & Mission</h1>
                    <p className="text-slate-500 mt-1">Manage the "Our Vision & Mission" section</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Header Settings */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
                    <h2 className="text-lg font-bold text-slate-800 border-b pb-2">Section Header</h2>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g. Our Vision & Mission"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Subtitle</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={2}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                            placeholder="Section subtitle..."
                            required
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Vision Section */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
                        <div className="flex items-center justify-between border-b pb-2">
                            <div className="flex items-center gap-2 text-blue-600">
                                <Star size={20} />
                                <h2 className="text-lg font-bold text-slate-800">Vision</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold uppercase ${formData.content_data.vision.is_active ? 'text-green-500' : 'text-slate-400'}`}>
                                    {formData.content_data.vision.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <StatusToggle
                                    active={formData.content_data.vision.is_active}
                                    onToggle={() => handleToggle('vision')}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                            <input
                                type="text"
                                name="vision_title"
                                value={formData.content_data.vision.title}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Content</label>
                            <textarea
                                name="vision_content"
                                value={formData.content_data.vision.content}
                                onChange={handleInputChange}
                                rows={5}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Icon</label>
                            <select
                                name="vision_icon"
                                value={formData.content_data.vision.icon}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            >
                                {iconOptions.map(opt => (
                                    <option key={opt.name} value={opt.name}>{opt.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Mission Section */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 space-y-4">
                        <div className="flex items-center justify-between border-b pb-2">
                            <div className="flex items-center gap-2 text-blue-600">
                                <Building size={20} />
                                <h2 className="text-lg font-bold text-slate-800">Mission</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold uppercase ${formData.content_data.mission.is_active ? 'text-green-500' : 'text-slate-400'}`}>
                                    {formData.content_data.mission.is_active ? 'Active' : 'Inactive'}
                                </span>
                                <StatusToggle
                                    active={formData.content_data.mission.is_active}
                                    onToggle={() => handleToggle('mission')}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                            <input
                                type="text"
                                name="mission_title"
                                value={formData.content_data.mission.title}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Content</label>
                            <textarea
                                name="mission_content"
                                value={formData.content_data.mission.content}
                                onChange={handleInputChange}
                                rows={5}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1">Icon</label>
                            <select
                                name="mission_icon"
                                value={formData.content_data.mission.icon}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            >
                                {iconOptions.map(opt => (
                                    <option key={opt.name} value={opt.name}>{opt.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 text-white px-8 py-3 rounded-xl hover:bg-primary-700 disabled:opacity-50 shadow-lg shadow-primary-500/30 transition-all font-bold text-lg"
                    >
                        <Save size={20} />
                        {saving ? 'Saving Changes...' : 'Save All Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AboutAhamVisionMissionPage;
